import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Spinner, Alert, Badge, Form, InputGroup,
  Modal, Dropdown,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import inboundApi from '../../config/inboundApi';

export default function GroupsTab() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await inboundApi.get('/groups', { params });
      setGroups(res.data);
    } catch {
      setMessage({ type: 'danger', text: 'Failed to load groups' });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await inboundApi.post('/groups', { name: createName.trim(), description: createDesc.trim() });
      setShowCreate(false);
      setCreateName('');
      setCreateDesc('');
      setMessage({ type: 'success', text: 'Group created' });
      fetchGroups();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.detail || 'Failed to create group' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await inboundApi.delete(`/groups/${id}`);
      setMessage({ type: 'success', text: 'Group deleted' });
      fetchGroups();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.detail || 'Failed to delete group' });
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <InputGroup style={{ maxWidth: 300 }}>
          <Form.Control
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          Add group
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No groups yet. Click "Add group" to create one.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Members</th>
                  <th>Description</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <span
                        style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: 600 }}
                        onClick={() => navigate(`/domain-admin/groups/${g.id}`)}
                      >
                        {g.name}
                      </span>
                      {g.is_default && (
                        <Badge bg="info" className="ms-2">Default</Badge>
                      )}
                    </td>
                    <td>{g.member_count}</td>
                    <td>
                      <small className="text-muted">{g.description || '-'}</small>
                    </td>
                    <td className="text-end">
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="link" size="sm" className="text-muted p-0" style={{ lineHeight: 1 }}>
                          &#x22EE;
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => navigate(`/domain-admin/groups/${g.id}`)}>
                            View / Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleDelete(g.id)}
                            disabled={g.is_default}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Group Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="Group name"
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
              placeholder="Optional description"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating || !createName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
